/**
 * Attribute-agnostic single-select toggle for a `PropertyFilter` chip
 * inside a `PropertyFilterGroup`. Pure and non-mutating.
 *
 * The FilterBar rehydrate step expects the root of the group to hold at
 * most two children: an optional top-level `asin` `PropertyFilter` and
 * one nested `PropertyFilterGroup` for every other attribute filter.
 * The target chip lives inside the nested group; nothing is written at
 * the root.
 *
 * Caller-provided `isMemberOfSelectGroup` predicate decides which
 * existing chips constitute the "select group" — the set of chips that
 * should be replaced by the target. That lets simple single-select
 * attributes strip by `propertyKey` while attributes that pack multiple
 * logical dimensions into one key (like `validationStatus`, which
 * prefixes the value with a bar identifier) can strip by value prefix
 * and preserve chips belonging to sibling dimensions.
 *
 * Toggle-off behavior: if the exact target chip
 * (`(newFilter.propertyKey, newFilter.value)`) already exists in the
 * nested group, all select-group members are stripped and the target is
 * not re-appended.
 */

import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "./searchSideBarConnector";

/**
 * Returns a fresh `PropertyFilterGroup` with the target token added,
 * replaced, or toggled off per the rules above. `current` is never
 * mutated. Non-matching tokens in the nested group and the root-level
 * `asin` filter are preserved untouched.
 */
export function toggleSingleSelectFilterToken(
  current: PropertyFilterGroup,
  newFilter: PropertyFilter,
  isMemberOfSelectGroup: (filter: PropertyFilter) => boolean
): PropertyFilterGroup {
  const asinFilter = current.filters.find(
    (f): f is PropertyFilter =>
      f.type === "PropertyFilter" && f.propertyKey === "asin"
  );
  const attributeGroup = current.filters.find(
    (f): f is PropertyFilterGroup => f.type === "PropertyFilterGroup"
  );

  const existingGroupFilters: ReadonlyArray<
    PropertyFilter | PropertyFilterGroup
  > = attributeGroup?.filters ?? [];

  const isExactTarget = (
    f: PropertyFilter | PropertyFilterGroup
  ): f is PropertyFilter =>
    f.type === "PropertyFilter" &&
    f.propertyKey === newFilter.propertyKey &&
    f.value === newFilter.value;

  const hasTarget = existingGroupFilters.some(isExactTarget);

  const strippedGroupFilters = existingGroupFilters.filter(
    (f) => f.type !== "PropertyFilter" || !isMemberOfSelectGroup(f)
  );

  const nextGroupFilters: Array<PropertyFilter | PropertyFilterGroup> =
    hasTarget
      ? [...strippedGroupFilters]
      : [...strippedGroupFilters, newFilter];

  const rootFilters: Array<PropertyFilter | PropertyFilterGroup> = [];
  if (asinFilter !== undefined) {
    rootFilters.push(asinFilter);
  }
  if (nextGroupFilters.length > 0) {
    rootFilters.push({
      type: "PropertyFilterGroup",
      operation: attributeGroup?.operation ?? "and",
      filters: nextGroupFilters,
    });
  }

  return {
    type: "PropertyFilterGroup",
    operation: current.operation,
    filters: rootFilters,
  };
}
